import type { JSX } from 'react';
import { LEISTUNGSPHASEN, phasenOhneGruppe } from '@asas/domain/lph-gruppen';
import type { LphGroup } from '@asas/domain';

import { alsEingabe, LPH_GRUPPEN_HOECHSTENS, phasenAnzeige } from './lph-gruppierung';
import type { GruppenEntwurf, GruppenMeldungen, Vorschaueintrag } from './lph-gruppierung';

/**
 * Die REINE Ansicht der LPH-Gruppierung (AC-305): lädt nichts, ruft nichts auf.
 * Laden, Speichern und Fehler stehen in `LphGruppierungBereich`.
 */
export interface LphGruppierungAnsichtProps {
  readonly geoeffnet: boolean;
  readonly onOeffnen: () => void;
  /** `null`, solange nichts geladen ist. */
  readonly entwurf: readonly GruppenEntwurf[] | null;
  readonly geaendert: boolean;
  readonly laedt: boolean;
  readonly sendet: boolean;
  /** Nach einer Ablehnung (403) bis zum Neuladen der Seite gesperrt. */
  readonly gesperrt: boolean;
  readonly ladefehler: string | null;
  readonly fehler: string | null;
  readonly meldung: string | null;
  /** Gesetzt, sobald Neuanmelden angeboten wird — nach Laden wie nach Speichern. */
  readonly anmeldeUrl: string | null;
  /** Ob die Eingaben für die Neuanmeldung gesichert wurden. */
  readonly anmeldeHinweis: string | null;
  readonly meldungen: GruppenMeldungen;
  /** Inzwischen gespeicherter Stand neben eigenen Eingaben (Konflikt). */
  readonly hinweis: readonly LphGroup[] | null;
  /** `null`: ohne `work_package_library:read` — die Vorschau entfällt (AK 4). */
  readonly vorschau: readonly Vorschaueintrag[] | null;
  readonly vorschauFehler: string | null;
  readonly onNeuLaden: () => void;
  readonly onUmbenennen: (index: number, label: string) => void;
  readonly onPhase: (index: number, phase: number) => void;
  readonly onHinzufuegen: () => void;
  readonly onEntfernen: (index: number) => void;
  readonly onVerwerfen: () => void;
  readonly onSpeichern: () => void;
  /** Sichert die aktuellen Eingaben, bevor der Link zur Anmeldung führt. */
  readonly onNeuAnmelden: () => void;
}

function gruppierungAnzeige(gruppen: readonly LphGroup[]): string {
  return gruppen.map((g) => `${g.label} (${phasenAnzeige(g.phases)})`).join('; ');
}

export function LphGruppierungAnsicht(props: LphGruppierungAnsichtProps): JSX.Element {
  const { entwurf, meldungen } = props;
  const gesperrtFuerEingaben = props.laedt || props.sendet || props.gesperrt;

  return (
    <section aria-labelledby="lph-gruppierung-titel">
      <h2 id="lph-gruppierung-titel">LPH-Gruppierung</h2>
      {!props.geoeffnet ? (
        <button type="button" onClick={props.onOeffnen}>
          LPH-Gruppierung öffnen
        </button>
      ) : (
        <>
          <p>
            <button
              type="button"
              onClick={props.onNeuLaden}
              disabled={props.laedt || props.sendet || props.gesperrt}
            >
              Neu laden
            </button>
            {props.laedt && <span> Wird geladen …</span>}
          </p>
          {props.ladefehler !== null && <p role="alert">{props.ladefehler}</p>}
          {entwurf !== null && (
            <>
              <table>
                <thead>
                  <tr>
                    <th scope="col">Gruppe</th>
                    {LEISTUNGSPHASEN.map((phase) => (
                      <th key={phase} scope="col">
                        LPH {phase}
                      </th>
                    ))}
                    <th scope="col">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {entwurf.map((gruppe, index) => {
                    const amFeld = meldungen.jeGruppe.get(index);
                    const labelFehlerId = `lph-gruppe-${String(index)}-label-fehler`;
                    const phasenFehlerId = `lph-gruppe-${String(index)}-phasen-fehler`;

                    return (
                      <tr key={index}>
                        <td>
                          <input
                            type="text"
                            aria-label={`Bezeichnung der Gruppe ${String(index + 1)}`}
                            value={gruppe.label}
                            disabled={gesperrtFuerEingaben}
                            aria-invalid={amFeld?.label !== undefined}
                            aria-describedby={
                              amFeld?.label !== undefined ? labelFehlerId : undefined
                            }
                            onChange={(e) => {
                              props.onUmbenennen(index, e.target.value);
                            }}
                          />
                          {amFeld?.label !== undefined && (
                            <small id={labelFehlerId} role="alert">
                              {amFeld.label}
                            </small>
                          )}
                          {amFeld?.phasen !== undefined && (
                            <small id={phasenFehlerId} role="alert">
                              {amFeld.phasen}
                            </small>
                          )}
                        </td>
                        {LEISTUNGSPHASEN.map((phase) => (
                          <td key={phase}>
                            <input
                              type="checkbox"
                              aria-label={`Gruppe ${String(index + 1)}: LPH ${String(phase)}`}
                              checked={gruppe.phasen.includes(phase)}
                              disabled={gesperrtFuerEingaben}
                              aria-invalid={amFeld?.phasen !== undefined}
                              aria-describedby={
                                amFeld?.phasen !== undefined ? phasenFehlerId : undefined
                              }
                              onChange={() => {
                                props.onPhase(index, phase);
                              }}
                            />
                          </td>
                        ))}
                        <td>
                          <button
                            type="button"
                            disabled={gesperrtFuerEingaben || entwurf.length <= 1}
                            title={
                              entwurf.length <= 1 ? 'Mindestens eine Gruppe bleibt.' : undefined
                            }
                            onClick={() => {
                              props.onEntfernen(index);
                            }}
                          >
                            Entfernen
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p data-testid="ohne-gruppe">
                Ohne Gruppe: {phasenAnzeige(phasenOhneGruppe(alsEingabe(entwurf)))}
              </p>
              <p>
                <button
                  type="button"
                  disabled={gesperrtFuerEingaben || entwurf.length >= LPH_GRUPPEN_HOECHSTENS}
                  onClick={props.onHinzufuegen}
                >
                  Gruppe hinzufügen
                </button>
              </p>
              {props.hinweis !== null && (
                <p data-testid="konflikt-hinweis">
                  Inzwischen gespeichert: {gruppierungAnzeige(props.hinweis)}. Ihre Eingaben sind
                  erhalten; Speichern ersetzt diesen Stand, „Änderungen verwerfen“ übernimmt ihn.
                </p>
              )}
              {props.vorschau !== null && (
                <div data-testid="vorschau">
                  <h3>Stammarbeitspakete ohne passende Gruppe nach dem Speichern</h3>
                  {props.vorschauFehler !== null ? (
                    <p role="alert">{props.vorschauFehler}</p>
                  ) : props.vorschau.length === 0 ? (
                    <p>Keine.</p>
                  ) : (
                    <ul>
                      {props.vorschau.map(({ eintrag, schonHeute }) => (
                        <li key={eintrag.id}>
                          {eintrag.code !== null ? `${eintrag.code} ` : ''}
                          {eintrag.name} ({phasenAnzeige(eintrag.leistungsphasen)})
                          {schonHeute && ' — schon heute ohne passende Gruppe'}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              <p>
                <button
                  type="button"
                  disabled={gesperrtFuerEingaben || !props.geaendert}
                  onClick={props.onSpeichern}
                >
                  {props.sendet ? 'Wird gespeichert …' : 'Gruppierung speichern'}
                </button>{' '}
                <button
                  type="button"
                  disabled={gesperrtFuerEingaben || (!props.geaendert && props.hinweis === null)}
                  onClick={props.onVerwerfen}
                >
                  Änderungen verwerfen
                </button>
              </p>
            </>
          )}
          {props.fehler !== null && (
            <p role="alert">{[props.fehler, ...meldungen.allgemein].join(' ')}</p>
          )}
          {/* Nach Laden wie nach Speichern (apps/web/CLAUDE.md, Entwürfe). */}
          {props.anmeldeUrl !== null && (
            <p data-testid="neu-anmelden">
              <a href={props.anmeldeUrl} onClick={props.onNeuAnmelden}>
                Neu anmelden
              </a>
              {props.anmeldeHinweis !== null && ` ${props.anmeldeHinweis}`}
            </p>
          )}
        </>
      )}
      <p role="status">{props.meldung ?? ''}</p>
    </section>
  );
}
